import type { Address } from '../types/address';

/**
 * Whether Google placed a typed street line at street level. Google rarely
 * answers "not found": a street it can't place comes back as just the ZIP (no
 * street1), and a house number it can't place comes back as the bare street
 * with its pin somewhere along it. A typo in the street name is fine, since
 * Google corrects it and keeps the number. Mirrors the BE's
 * `geocodedStreetMatches` (handler/order.go), which refuses the same results
 * at reserve.
 */
export function geocodedStreetMatches(typed: string, geocoded: string | undefined): boolean {
  return !!geocoded && houseNumber(typed) === houseNumber(geocoded);
}

/** Leading digits of a street line, without leading zeros ("" for "Steele St"). */
function houseNumber(street1: string): string {
  return (street1.trim().match(/^\d*/)?.[0] ?? '').replace(/^0+/, '');
}

/**
 * Treat two addresses as the same place if their Google placeIds match,
 * else fall back to street1 + postalCode (case-insensitive, trimmed).
 *
 * Used to gate behaviors that should only fire for the same physical place,
 * e.g. preserving the user's apt/unit answer across an autocomplete re-pick
 * and writing back the publish-time unit to the profile address.
 */
export function addressesMatch(
  a: Address | null | undefined,
  b: Address | null | undefined,
): boolean {
  if (!a || !b) return false;
  if (a.placeId && b.placeId) return a.placeId === b.placeId;
  const norm = (s?: string) => s?.trim().toLowerCase() ?? '';
  return (
    !!a.street1 &&
    !!a.postalCode &&
    norm(a.street1) === norm(b.street1) &&
    norm(a.postalCode) === norm(b.postalCode)
  );
}

/**
 * A "complete" address suitable for publishing a listing — a real street (or an
 * explicitly-confirmed pin) plus city/state/postal/country. Mirrors
 * `AddressEditCard`'s `canSave`. Used to gate publish: an orderer's minimum
 * profile address (postal + country only) is intentionally NOT full, so a
 * lister with only the minimum is prompted to finish a real address at publish.
 */
export function isFullProfileAddress(addr: Address | null | undefined): boolean {
  return !!(
    addr &&
    (addr.street1 || addr.pinConfirmed) &&
    addr.city &&
    addr.stateProvince &&
    addr.postalCode &&
    addr.country
  );
}

/**
 * True when the text reads as a US ZIP (`80206`) or ZIP+4 (`80206-1234`).
 *
 * Used by the locale-search surfaces to route input: a ZIP resolves straight
 * to a locale/geocode with no Places call, while anything else is treated as a
 * place name and goes through region autocomplete. Deliberately US-only and
 * deliberately narrow — the fallback branch (autocomplete + geocode) handles
 * everything else, including foreign postal codes, which then fail the
 * existing US-only guards with the country error rather than a ZIP error.
 */
export function isPostalCodeInput(text: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(text.trim());
}
